import { faTimes, faGripDotsVertical } from '@fortawesome//pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain } from 'lodash';
import React, { useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import VoxelIconButton from '../../../../voxel/Buttons/VoxelIconButton';
import VoxelSwitch from '../../../../voxel/Forms/VoxelSwitch';
import VoxelText from '../../../../voxel/Text/VoxelText';
import { getColumnId } from './getColumnId';
import { getColumnName } from './getColumnName';
import { KeywordTableColumn, KeywordTableColumnSize, KeywordTableColumnTypes } from './models';
import { KeywordColumnPickerBag } from './useKeywordColumnPicker';
import VoxelSubtleButton from '../../../../voxel/Buttons/VoxelSubtleButton';
import VoxelDivider from '../../../../voxel/VoxelDivider';
import { usersApiService } from '../../../../../graphql/users/usersApiService';
import { FrontendUserEventGq } from '../../../../../graphql/users/queries/useFrontendLogUserEventGq';
import VoxelTooltipShort from '../../../../voxel/Tooltip/VoxelTooltipShort';

export interface KeywordColumnPickerShownColumnsProps {
  keywordColumnPickerBag: KeywordColumnPickerBag;
  project: ProjectGq;
  isProgressPage?: boolean;
}

export const KeywordColumnPickerShownColumns: React.FC<KeywordColumnPickerShownColumnsProps> = ({
  keywordColumnPickerBag,
  project,
  isProgressPage,
}) => {
  const { setColumnState, shownOrderedColumns } = keywordColumnPickerBag;
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();

  // Dragging
  const handleDragEnd = (result: DropResult, provided: ResponderProvided) => {
    if (!result.destination) return;

    setColumnState((prev) => {
      let nextShown = prev.filter((c) => c.order !== undefined);
      const nextHidden = prev.filter((c) => c.order === undefined);

      // Move in the array
      const [removed] = nextShown.splice(result.source.index, 1);
      nextShown.splice(result.destination.index, 0, removed);
      nextShown = nextShown.map((c, i) => ({ ...c, order: i }));

      return [...nextShown, ...nextHidden];
    });
  };

  // Removing
  const handleRemove = (removedColumn: KeywordTableColumn) => {
    if (removedColumn.type === KeywordTableColumnTypes.Keyword) return;

    // Document feature usage
    logUserEventMutation({
      event: FrontendUserEventGq.TABLE_SLOW_HIDE_COLUMN,
    });

    setColumnState((prev) =>
      chain(prev)
        .map((c) => {
          if (c.type === removedColumn.type && c.id === removedColumn.id)
            return { ...c, order: undefined };
          return c;
        })
        .orderBy((c) => c === undefined || c.order)
        .map((c, index) => {
          if (c.order === undefined) return c;
          if (c.order === index) return c;
          return { ...c, order: index };
        })
        .value()
    );
  };

  // Toggle URL
  const handleToggleUrl = (rankColumn: KeywordTableColumn) => {
    if (rankColumn.type === KeywordTableColumnTypes.Keyword) return;
    setColumnState((prev) =>
      chain(prev)
        .map((c) => {
          if (c.type === rankColumn.type && c.id === rankColumn.id) return { ...c, url: !c.url };
          return c;
        })
        .orderBy((c) => c === undefined || c.order)
        .map((c, index) => {
          if (c.order === undefined) return c;
          if (c.order === index) return c;
          return { ...c, order: index };
        })
        .value()
    );
  };

  // Toggle Traffic
  const handleToggleTraffic = (rankColumn: KeywordTableColumn) => {
    if (rankColumn.type === KeywordTableColumnTypes.Keyword) return;
    setColumnState((prev) =>
      chain(prev)
        .map((c) => {
          if (c.type === rankColumn.type && c.id === rankColumn.id)
            return { ...c, traffic: !c.traffic };
          return c;
        })
        .orderBy((c) => c === undefined || c.order)
        .map((c, index) => {
          if (c.order === undefined) return c;
          if (c.order === index) return c;
          return { ...c, order: index };
        })
        .value()
    );
  };

  // Toggle size
  const [lastResizeColumn, setLastResizeColumn] = useState<KeywordTableColumn>();
  const handleToggleSize = (column: KeywordTableColumn, size: KeywordTableColumnSize) => {
    if (lastResizeColumn !== column) {
      setLastResizeColumn(column);
      // Document feature usage
      logUserEventMutation({
        event: FrontendUserEventGq.TABLE_SLOW_RESIZE_COLUMN,
      });
    }

    setColumnState((prev) =>
      chain(prev)
        .map((c) => {
          if (c.type === column.type && c.id === column.id)
            return { ...c, size: size, sizePx: undefined };
          return c;
        })
        .orderBy((c) => c === undefined || c.order)
        .map((c, index) => {
          if (c.order === undefined) return c;
          if (c.order === index) return c;
          return { ...c, order: index };
        })
        .value()
    );
  };

  const getSizeName = (size: KeywordTableColumnSize) => {
    if (size === KeywordTableColumnSize.none) return 'auto size';
    if (size === KeywordTableColumnSize.xs) return 'x-small';
    if (size === KeywordTableColumnSize.sm) return 'small';
    if (size === KeywordTableColumnSize.md) return 'medium';
    if (size === KeywordTableColumnSize.lg) return 'large';
    if (size === KeywordTableColumnSize.xl) return 'x-large';
    return '?';
  };

  return (
    <>
      <div
        className={`
          py-2 px-4
        `}
      >
        <VoxelText variant="bread-normal" color="slight" className="!text-[13px]">
          Visible columns
        </VoxelText>
      </div>
      <VoxelDivider color="light" />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable">
          {(droppableProvided, droppableSnapshot) => (
            <div
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}
              className=""
            >
              {shownOrderedColumns.map((column, index) => (
                <Draggable
                  key={getColumnId(column)}
                  draggableId={getColumnId(column)}
                  index={index}
                >
                  {(draggableProvided, draggableSnapshot) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      style={draggableProvided.draggableProps.style}
                      className={`
                        select-none flex flex-row items-center justify-between pl-4 pr-2 
                        !top-auto !left-auto
                      `}
                    >
                      <div className="flex flex-row items-center gap-3 justify-start">
                        {/* Drag icon */}
                        <div
                          className={`
                          cursor-grab
                          text-voxel-gray-400 hover:text-voxel-gray-700
                        `}
                          {...draggableProvided.dragHandleProps}
                        >
                          <FontAwesomeIcon icon={faGripDotsVertical} />
                        </div>
                        {/* text */}
                        <div
                          className={`flex-grow ${column.type === KeywordTableColumnTypes.Rank && 'w-24'
                            }`}
                        >
                          <VoxelText ellipsis variant="table" color="dark">
                            {getColumnName(column, project)}
                          </VoxelText>
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-1 justify-end">
                        {/* Est. traffic */}
                        {column.type === KeywordTableColumnTypes.Rank && (
                          <VoxelSwitch
                            color="primary"
                            title="Traffic"
                            size="sm"
                            value={!!column.traffic}
                            onChange={() => handleToggleTraffic(column)}
                          />
                        )}
                        {/* Url */}
                        {column.type === KeywordTableColumnTypes.Rank && (
                          <VoxelSwitch
                            color="primary"
                            title="Url"
                            size="sm"
                            value={!!column.url}
                            onChange={() => handleToggleUrl(column)}
                          />
                        )}
                        {/* size */}
                        <VoxelTooltipShort message='Column size'>
                          <VoxelSubtleButton
                            className="font-sans font-medium !py-[5px]"
                            label={column.sizePx ? 'custom' : getSizeName(column.size)}
                            onClick={() => {
                              if (!!column.sizePx) {
                                handleToggleSize(column, column.size ?? KeywordTableColumnSize.xs);
                                return;
                              }

                              let nextSize = KeywordTableColumnSize.xs;
                              if (column.size === KeywordTableColumnSize.xs)
                                nextSize = KeywordTableColumnSize.sm;
                              if (column.size === KeywordTableColumnSize.sm)
                                nextSize = KeywordTableColumnSize.md;
                              if (column.size === KeywordTableColumnSize.md)
                                nextSize = KeywordTableColumnSize.lg;
                              if (column.size === KeywordTableColumnSize.lg)
                                nextSize = KeywordTableColumnSize.xl;
                              if (column.size === KeywordTableColumnSize.xl)
                                nextSize = KeywordTableColumnSize.none;
                              handleToggleSize(column, nextSize);
                            }}
                          />
                        </VoxelTooltipShort>
                        {/* remove button */}
                        <VoxelIconButton
                          size="xxs"
                          color="white"
                          onClick={() => handleRemove(column)}
                          disabled={column.type === KeywordTableColumnTypes.Keyword}
                          icon={<FontAwesomeIcon icon={faTimes} />}
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default KeywordColumnPickerShownColumns;
